import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import axios from "axios";

class PushNotificationService {
    constructor() {
        this.STORED_TOKEN_KEY = "device_push_token";
        this.STORED_PLATFORM_KEY = "device_push_platform";
    }

    async initialize() {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        // Register listeners BEFORE requesting permissions/registration
        this.addListeners();

        // Request permission
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === "prompt") {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive === "granted") {
            await PushNotifications.register();
        }
    }

    addListeners() {
        // Registration success
        PushNotifications.addListener("registration", async (token) => {
            // Store token locally for later re-association on login
            this.storeToken(token.value);
        });

        // Registration error
        PushNotifications.addListener("registrationError", (error) => {
            console.error("❌ Error on registration:", error);
        });

        // Push notification received
        PushNotifications.addListener(
            "pushNotificationReceived",
            (notification) => {}
        );

        // Push notification action performed
        PushNotifications.addListener(
            "pushNotificationActionPerformed",
            (notification) => {
                // Handle notification tap - navigate to appropriate screen
                this.handleNotificationAction(notification);
            }
        );
    }

    storeToken = (token) => {
        try {
            const platform = Capacitor.getPlatform();
            localStorage.setItem(this.STORED_TOKEN_KEY, token);
            localStorage.setItem(this.STORED_PLATFORM_KEY, platform);
        } catch (error) {
            console.error("Failed to store device token:", error);
        }
    };

    saveTokenToBackend = async (token, platform) => {
        try {
            await axios.post("account/device-token", {
                token,
                platform,
            });
            return true;
        } catch (error) {
            console.error("❌ Failed to save device token:", error);
            return false;
        }
    };

    /**
     * Save stored token to backend when user is authenticated
     * This should be called after login/authentication to associate
     * the device token with the current user
     */
    saveStoredTokenIfAuthenticated = async () => {
        try {
            const token = localStorage.getItem(this.STORED_TOKEN_KEY);
            const platform = localStorage.getItem(this.STORED_PLATFORM_KEY);

            if (token && platform) {
                const success = await this.saveTokenToBackend(token, platform);
                if (!success) {
                    console.error("Failed save token to backend");
                }
            } else {
                // Token not in localStorage (cleared/first time) but device may still be registered
                // Re-register to get the token again from OS
                await this.reRegisterForToken();
            }
        } catch (error) {
            console.error("❌ Failed to save stored token:", error);
        }
    };

    /**
     * Re-register with OS to retrieve device token
     * This is safe to call multiple times - OS will return existing token
     * Useful when localStorage is cleared but device is already registered
     */
    reRegisterForToken = async () => {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        try {
            // Check if we still have permission
            const permStatus = await PushNotifications.checkPermissions();

            if (permStatus.receive === "granted") {
                // This will trigger the 'registration' listener with the token
                await PushNotifications.register();

                // Wait a bit for the registration callback to fire and save token
                await new Promise((resolve) => setTimeout(resolve, 2000));

                // Try to save again now that token should be in localStorage
                const token = localStorage.getItem(this.STORED_TOKEN_KEY);
                const platform = localStorage.getItem(this.STORED_PLATFORM_KEY);

                if (token && platform) {
                    await this.saveTokenToBackend(token, platform);
                } else {
                    console.warn(
                        "⚠️ Token not retrieved after re-registration"
                    );
                }
            }
        } catch (error) {
            console.error("❌ Failed to re-register for token:", error);
        }
    };

    handleNotificationAction = (notification) => {
        // Implement navigation based on notification data
        const data = notification.notification.data;
        if (data.type === "subscription_cancelled") {
            window.location.href = "/account";
        } else if (
            data.type === "product_event_emerge" ||
            data.type === "tracking_event_emerge"
        ) {
            window.location.href = "/dashboard";
        }
    };

    removeAllListeners = async () => {
        await PushNotifications.removeAllListeners();
    };
}

export default PushNotificationService;
